import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const eur = (v) => `€ ${String(v ?? "0.00").replace(".", ",")}`;
const INPUT = "w-full border-2 border-foreground bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

// Creditfactuur voor een betaalde factuur: bedrag kiezen, reden, refund via Mollie,
// abonnement beëindigen en (optioneel) AVG-verwijdering van de klantdata.
export default function CreditNoteDialog({ invoice, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("full");
  const [custom, setCustom] = useState("");
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState(true);
  const [cancelSub, setCancelSub] = useState(true);
  const [deleteData, setDeleteData] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setPreview(null);
    api.get(`/admin/invoices/${invoice.payment_id}/credit-preview`)
      .then(({ data }) => { setPreview(data); setRefund(!!data.refundable); setCancelSub(!!data.subscription_active); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [invoice, onClose]);

  const amount = mode === "full" ? preview?.full : mode === "prorata" ? preview?.prorata : custom;

  const submit = async (e) => {
    e.preventDefault();
    const steps = [`creditfactuur van ${eur(amount)}`, refund && "terugbetaling via Mollie", cancelSub && "abonnement direct beëindigen", deleteData && "ACCOUNT EN CLUBDATA VERWIJDEREN (onomkeerbaar)"].filter(Boolean);
    if (!window.confirm(`Doorgaan voor ${invoice.customer_name}?\n\n• ${steps.join("\n• ")}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/invoices/${invoice.payment_id}/credit`, {
        mode, amount: mode === "custom" ? custom : null, reason, refund_via_mollie: refund, cancel_subscription: cancelSub, delete_data: deleteData,
      });
      toast.success(`Creditfactuur ${data.credit_note.invoice_number} aangemaakt (${eur(data.credit_note.amount.replace("-", ""))})`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Crediteren mislukt.");
    }
    setBusy(false);
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none heavy-shadow" data-testid="credit-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight flex items-center gap-2">
            <Undo2 className="w-5 h-5" /> Creditfactuur
          </DialogTitle>
          <DialogDescription className="text-xs">
            Op factuur <b>{invoice?.invoice_number}</b> · {invoice?.customer_name} · betaald {eur(invoice?.amount)}
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Berekenen…</div>
        ) : (
          <form onSubmit={submit} className="space-y-4 text-sm">
            <fieldset className="space-y-2" data-testid="credit-mode-group">
              <legend className="stat-label mb-1">Bedrag</legend>
              {[
                ["full", `Volledig — ${eur(preview.full)}`, preview.already_credited !== "0.00" ? `(al gecrediteerd: ${eur(preview.already_credited)})` : ""],
                ["prorata", `Pro rata — ${eur(preview.prorata)}`, `${preview.months_remaining} van 12 maanden resterend`],
                ["custom", "Eigen bedrag", ""],
              ].map(([val, label, hint]) => (
                <label key={val} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="mode" value={val} checked={mode === val} onChange={() => setMode(val)} data-testid={`credit-mode-${val}`} className="accent-primary" />
                  <span className="font-bold">{label}</span>{hint && <span className="text-xs text-muted-foreground">{hint}</span>}
                </label>
              ))}
              {mode === "custom" && (
                <input type="number" step="0.01" min="0.01" max={preview.full} value={custom} onChange={(e) => setCustom(e.target.value)} required
                  placeholder={`max. ${preview.full}`} data-testid="credit-custom-amount" className={`${INPUT} font-mono-num ml-6 w-40`} />
              )}
            </fieldset>
            <div>
              <label className="stat-label">Reden (komt op de creditfactuur)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} maxLength={200}
                placeholder="bv. opzegging binnen proefperiode / ontevreden over functionaliteit" data-testid="credit-reason" className={`${INPUT} mt-1`} />
            </div>
            <div className="space-y-2 border-2 border-foreground p-3 bg-muted/30">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={refund} disabled={!preview.refundable} onChange={(e) => setRefund(e.target.checked)} data-testid="credit-refund" className="mt-0.5 accent-primary" />
                <span><b>Terugbetalen via Mollie</b> — refund op de oorspronkelijke betaling{!preview.refundable && <span className="text-xs text-muted-foreground"> (niet mogelijk voor deze betaling; betaal handmatig terug)</span>}</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={cancelSub} onChange={(e) => setCancelSub(e.target.checked)} data-testid="credit-cancel-sub" className="mt-0.5 accent-primary" />
                <span><b>Abonnement direct beëindigen</b> — geen verlenging meer, account naar gratis/alleen-lezen{!preview.subscription_active && <span className="text-xs text-muted-foreground"> (geen actief abonnement gevonden)</span>}</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={deleteData} onChange={(e) => setDeleteData(e.target.checked)} data-testid="credit-delete-data" className="mt-0.5 accent-destructive" />
                <span><b className="text-destructive">Account en clubdata verwijderen (AVG)</b> — onomkeerbaar; facturen blijven geanonimiseerd bewaard{preview.anonymized && <span className="text-xs text-muted-foreground"> (al verwijderd)</span>}</span>
              </label>
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="w-4 h-4 shrink-0 text-secondary" />
              De creditfactuur verschijnt in de wachtrij als "te versturen"; de klant krijgt direct een bevestiging van de beëindiging.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} disabled={busy} data-testid="credit-cancel-btn"
                className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold text-sm">Annuleren</button>
              <button type="submit" disabled={busy || !reason.trim() || (mode === "custom" && !custom)} data-testid="credit-submit-btn"
                className="btn-brutal bg-destructive text-white px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Creditfactuur aanmaken
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
